import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import Ico from './ui/Ico';

export default function Tutorial() {
  const [selectedModule, setSelectedModule] = useState('dashboard');
  const [activeTab, setActiveTab] = useState('visao-geral');
  const [searchTerm, setSearchTerm] = useState('');

  const modules = {
    dashboard: {
      title: 'Dashboard Principal',
      icon: <Ico e="📊" bare />,
      description: 'Centro de controle do seu CRM com métricas e insights de IA',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `O Dashboard é o coração do NexoSynX - sua central de comando para todas as atividades de vendas. É a primeira tela que você vê ao fazer login e oferece uma visão consolidada em tempo real de todas as métricas importantes do seu negócio.

**Principais Componentes:**

• **Métricas Principais:** Cards superiores com KPIs essenciais
• **Assistente de Voz:** Interface de comando por voz integrada  
• **Previsão IA:** Análises preditivas baseadas em inteligência artificial
• **Insights Automáticos:** Alertas e recomendações inteligentes
• **Gamificação:** Acompanhamento de performance e rankings`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Para que serve o Dashboard:**

• **Monitoramento em Tempo Real:** Acompanhe performance de vendas instantaneamente
• **Tomada de Decisão:** Base de dados para decisões estratégicas
• **Controle de KPIs:** Monitore indicadores-chave de performance
• **Insights Preditivos:** Antecipe tendências com IA
• **Acesso Rápido:** Hub para navegar para outras funcionalidades
• **Motivação da Equipe:** Gamificação e rankings em tempo real

**Benefícios:**
- Visibilidade total do pipeline de vendas
- Identificação rápida de oportunidades e riscos
- Aumento da produtividade da equipe
- Decisões baseadas em dados reais`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Passo a Passo:**

**1. Análise Matinal (5 min)**
• Verifique os cards de métricas principais ao fazer login
• Compare com metas estabelecidas
• Identifique tendências (crescimento/queda)

**2. Comandos de Voz**  
• Clique em "Iniciar Comando de Voz"
• Use comandos como: "Ir para leads", "Ver estatísticas"
• "Adicionar lead João Silva email joao@empresa.com"

**3. Análise de Previsões IA**
• Observe o nível de confiança das previsões
• Leia fatores positivos e riscos identificados
• Use insights para planejar ações do dia

**4. Acompanhamento de Gamificação**
• Verifique sua posição no ranking
• Monitore progresso das metas
• Celebre conquistas da equipe`
        },
        'dicas': {
          title: 'Dicas',
          content: `**Dicas de Produtividade:**

• **Rotina Diária:** Sempre inicie verificando o dashboard
• **Comandos Rápidos:** Use voz para ações repetitivas  
• **Metas Visuais:** Acompanhe gamificação como motivação
• **Alertas:** Preste atenção em leads frios destacados

**Boas Práticas:**
- Atualize métricas várias vezes ao dia
- Aja imediatamente nos insights de IA
- Compartilhe sucessos com a equipe
- Configure alertas personalizados

**Cuidados:**
- Não ignore alertas de leads frios
- Verifique confiança das previsões antes de decisões importantes
- Mantenha microfone habilitado para comandos de voz`
        }
      }
    },
    leads: {
      title: 'Gestão de Leads',
      icon: <Ico e="👥" bare />,
      description: 'Sistema completo de gestão de leads com análise de IA integrada',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `O módulo de Leads é onde toda a magia de vendas acontece. É um sistema completo de gestão de prospects que combina funcionalidades tradicionais com inteligência artificial avançada para maximizar suas conversões.

**Funcionalidades Principais:**

• **Gestão Completa:** Adicione, edite e organize todos seus leads
• **Análise IA:** Score automático e análise de sentimento  
• **Qualificação Inteligente:** Sistema de pontuação 0-100
• **Pipeline Visual:** Acompanhe leads pelo funil de vendas
• **Histórico Detalhado:** Registre todas as interações`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Objetivos do Sistema de Leads:**

• **Centralizar Prospecção:** Todos os leads em um só lugar
• **Automatizar Qualificação:** IA analisa e pontua automaticamente
• **Priorizar Ações:** Foque nos leads com maior potencial
• **Acompanhar Jornada:** Visualize todo o processo de conversão
• **Aumentar Conversão:** Dados inteligentes para melhor abordagem

**Tipos de Leads Suportados:**
- Lead Novo (recém-captado)
- Lead Qualificado (com potencial confirmado)  
- Lead Quente (alta probabilidade)
- Lead Frio (precisa reativação)`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Adicionando Novo Lead:**

**1. Clique "Adicionar Lead"**
**2. Preencha informações obrigatórias:**
• Nome completo do prospect
• Email válido para contato  
• Telefone (recomendado)
• Empresa (importante para score IA)
• Selecione o funil apropriado

**3. Análise com IA:**
• Clique no botão roxo "Analisar com IA"
• Aguarde processamento (15-30 segundos)
• Analise score, sentimento e recomendações

**4. Ações Baseadas no Score:**
• Score 90-100: Contato imediato
• Score 80-89: Agendar reunião 24h
• Score 60-79: Follow-up estruturado  
• Score 40-59: Nutrição com conteúdo
• Score 0-39: Revisar ou descartar`
        },
        'dicas': {
          title: 'Dicas',
          content: `**Estratégias de Conversão:**

• **Qualidade vs Quantidade:** Prefira leads bem qualificados
• **Timing é Tudo:** Contate leads quentes em até 5 minutos
• **Personalização:** Use insights IA para abordagem personalizada
• **Acompanhamento:** Nunca deixe lead sem resposta por mais de 24h

**Interpretando Scores:**
- 90+ pontos: Lead premium, prioridade máxima
- 70-89: Bom potencial, acompanhamento estruturado  
- 50-69: Médio potencial, nutrição necessária
- Abaixo 50: Baixo potencial, revisar dados

**Sentimentos:**
Positivo: Interesse ativo demonstrado
Neutro: Interesse moderado  
Negativo: Resistência ou objeções`
        }
      }
    },
    funis: {
      title: 'Funis de Vendas',
      icon: <Ico e="🔄" bare />,
      description: 'Sistema de funis personalizáveis para diferentes tipos de vendas',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `Os Funis de Vendas representam o processo que um lead percorre desde o primeiro contato até se tornar cliente. O NexoSynX oferece funis pré-configurados e personalizáveis para diferentes estratégias de vendas.

**Funis Disponíveis:**

• **B2B:** Para vendas corporativas com ciclo longo
• **B2C:** Para consumidor final com ciclo rápido  
• **Pós-Venda:** Upsell, cross-sell e renovações
• **Marketing:** Captação e nutrição de leads
• **Personalizados:** Crie funis específicos para seu negócio`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Por que usar Funis de Vendas:**

• **Organização:** Cada tipo de venda tem processo específico
• **Controle:** Acompanhe exatamente onde cada lead está
• **Otimização:** Identifique gargalos no processo
• **Previsibilidade:** Calcule receita baseada nos estágios
• **Padronização:** Garanta que a equipe siga o mesmo processo

**Métricas Importantes:**
- Taxa de Conversão por estágio
- Tempo médio em cada etapa
- Volume de leads por estágio  
- Receita potencial por funil`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Criando um Funil Personalizado:**

**1. Acesse "Funis" no menu**
**2. Clique "Criar Novo Funil"**
**3. Configure:**
• Nome descritivo do funil
• Tipo (B2B, B2C, etc.)
• Descrição clara do propósito

**4. Defina Estágios:**
• Máximo 7-8 estágios (recomendado)
• Nomes claros para cada etapa
• Critérios de passagem entre estágios

**5. Ativar e Testar:**
• Configure o funil como ativo
• Associe leads para testar o fluxo
• Monitore métricas e ajuste conforme necessário`
        },
        'dicas': {
          title: 'Dicas',
          content: `**Melhores Práticas:**

• **Simplicidade:** Funis com muitos estágios confundem
• **Clareza:** Nomes de estágios devem ser autoexplicativos
• **Flexibilidade:** Permita movimentação entre estágios
• **Monitoramento:** Acompanhe métricas regularmente

**Otimização Contínua:**
- Identifique estágios com maior perda
- Teste diferentes abordagens
- Colete feedback da equipe de vendas
- Ajuste baseado em dados reais

**Automações Recomendadas:**
- Alertas quando lead fica muito tempo parado
- Notificações de oportunidades quentes
- Reports automáticos de performance`
        }
      }
    },
    gamificacao: {
      title: 'Sistema de Gamificação',
      icon: <Ico e="🎮" bare />,
      description: 'Motivação e engajamento através de pontos, níveis e rankings',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `A Gamificação transforma o trabalho de vendas em uma experiência envolvente e motivadora. Aplica elementos de jogos para aumentar engajamento, produtividade e satisfação da equipe.

**Elementos do Sistema:**

• **Pontos:** Ganhe por cada ação realizada no CRM
• **Níveis:** Evolua conforme acumula experiência  
• **Badges:** Conquistas especiais por marcos importantes
• **Rankings:** Compare performance com a equipe
• **Metas:** Desafios mensais personalizados`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Benefícios da Gamificação:**

• **Motivação:** Torna atividades de vendas mais divertidas
• **Competição Saudável:** Estimula superação pessoal e em equipe
• **Reconhecimento:** Valoriza conquistas e esforços
• **Transparência:** Performance visível para todos
• **Engajamento:** Aumenta uso consistente do CRM

**Impactos Comprovados:**
- Aumento de 30% na produtividade
- Maior retenção da equipe de vendas  
- Melhoria na qualidade dos dados no CRM
- Cultura mais colaborativa e positiva`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Sistema de Pontuação:**

**Ações com Leads (50-200 pts):**
• Adicionar novo lead: +50 pontos
• Primeira interação: +30 pontos
• Análise IA de lead: +10 pontos
• Converter em venda: +200 pontos

**Tarefas e Atividades (25-35 pts):**
• Completar tarefa: +25 pontos  
• Completar no prazo: +35 pontos
• Criar tarefa para equipe: +15 pontos

**Uso de IA e Automação (5-50 pts):**
• Comando de voz: +5 pontos
• Análise de contrato: +20 pontos
• Usar chatbot: +10 pontos

**Bonus Especiais (100-500 pts):**
• Meta mensal atingida: +500 pontos
• Primeiro lugar ranking: +300 pontos
• Lead score > 90: +100 pontos`
        },
        'dicas': {
          title: 'Dicas',
          content: `**Estratégias para Subir no Ranking:**

• **Consistência:** Ações diárias valem mais que surtos esporádicos
• **Qualidade:** Leads bem qualificados geram mais pontos
• **Colaboração:** Ajudar colegas também pontua
• **Inovação:** Explore novas funcionalidades do sistema

**Metas Inteligentes:**
- Defina objetivos realistas e progressivos
- Comemore pequenas vitórias
- Use badges como marcos de progresso
- Compartilhe conquistas com a equipe

**Evite Armadilhas:**
- Não foque apenas em pontos, qualidade importa
- Não compare-se excessivamente com outros
- Mantenha o foco no resultado real de vendas`
        }
      }
    },
    activities: {
      title: 'Atividades',
      icon: <Ico e="🎯" bare />,
      description: 'Registros históricos de interações realizadas com leads e clientes',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `O módulo de Atividades é o seu diário completo de interações com clientes e prospects. É onde você documenta TUDO que aconteceu - ligações realizadas, emails enviados, reuniões feitas, propostas apresentadas.

**Conceito Principal:**
Atividades são registros **históricos** e **documentais** do que JÁ ACONTECEU. Diferente de tarefas (que são futuras), atividades capturam o histórico completo do relacionamento.

**Funcionalidades Principais:**

• **Histórico Completo:** Registre todas as interações realizadas
• **Timeline Visual:** Veja cronologia de relacionamento com cliente
• **Categorização:** Organize por tipo (ligação, email, reunião, etc.)
• **Análise Temporal:** Identifique padrões de comunicação
• **Relatórios:** Compile atividades por período ou vendedor`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Para que servem as Atividades:**

• **DOCUMENTAR interações realizadas** - registro histórico completo
• **COMPROVAR esforços de vendas** - evidência de trabalho realizado  
• **ANALISAR padrões de comunicação** - frequência e qualidade de contatos
• **TRANSFERIR conhecimento** - passar contexto para outros vendedores
• **CUMPRIR compliance** - auditoria e conformidade regulatória

**Exemplos Práticos de Atividades:**

**Exemplos CORRETOS (Atividades):**
• "Ligação realizada para João Silva - 15 minutos - cliente interessado no produto X"
• "Email enviado com proposta comercial no valor de R$ 50.000"  
• "Reunião realizada via Teams - apresentação da solução - 1h30"
• "WhatsApp respondido - esclarecimento sobre prazo de entrega"
• "Demo do produto realizada - cliente aprovou funcionalidades"
• "Proposta assinada e recebida por email - negócio fechado"

**Diferença Crucial:**
- ATIVIDADE: "Liguei para o cliente" (passado - JÁ FEZ)
- TAREFA: "Preciso ligar para cliente" (futuro - VAI FAZER)`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Registrando Nova Atividade:**

**1. Clique "Nova Atividade"**
**2. Selecione o Lead/Cliente relacionado**
**3. Escolha o Tipo de Atividade:**
• Ligação
• Email  
• Reunião
• WhatsApp
• Proposta
• Demo/Apresentação

**4. Descreva o que aconteceu:**
• **O QUE** foi feito
• **QUANDO** aconteceu  
• **RESULTADO** obtido
• **PRÓXIMOS PASSOS** (se aplicável)

**5. Marque como "Concluída"** (sempre!)

**Exemplo de Atividade Bem Registrada:**
- **Tipo:** Reunião
- **Cliente:** Maria Santos - Empresa XYZ  
- **Data:** 15/09/2024 14:30
- **Descrição:** "Reunião comercial realizada via Teams. Apresentei solução completa incluindo módulos A, B e C. Cliente demonstrou interesse especial no módulo B para automação. Solicitou proposta formal até sexta-feira. Próximo passo: enviar proposta customizada."
- **Status:** Concluída `
        },
        'dicas': {
          title: 'Dicas',
          content: `**Boas Práticas para Atividades:**

• **Registre IMEDIATAMENTE:** Não deixe para depois, detalhes se perdem
• **Seja ESPECÍFICO:** "Cliente interessado" vs "Cliente pediu proposta de R$ 30k"
• **Inclua RESULTADOS:** Sempre anote o que foi conquistado ou aprendido
• **Use DADOS:** Valores, prazos, números sempre que possível
• **Timeline COMPLETA:** Todo contato deve virar atividade

**Organizando seu Histórico:**
- Use filtros por período para análises
- Exporte relatórios mensais/trimestrais  
- Analise padrões: quais atividades geram mais vendas
- Transfira conhecimento para equipe

**IMPORTANTE - Não Confundir:**
"Preciso ligar amanhã" = TAREFA (futuro)
"Liguei hoje às 14h" = ATIVIDADE (passado)
"Agendar reunião" = TAREFA (ação futura)  
"Reunião realizada" = ATIVIDADE (ação concluída)`
        }
      }
    },
    tasks: {
      title: 'Tarefas',
      icon: <Ico e="📝" bare />,
      description: 'Lista de ações futuras que precisam ser executadas (To-Do List)',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `O módulo de Tarefas é sua **To-Do List inteligente** - um sistema completo para planejar, organizar e executar todas as ações que PRECISAM SER FEITAS no seu processo de vendas.

**Conceito Principal:**
Tarefas são ações **futuras** e **acionáveis** que você planeja executar. São seu planejamento estratégico transformado em passos práticos com prazos e responsáveis.

**Funcionalidades Principais:**

• **Planejamento de Ações:** Defina o que precisa ser feito e quando
• **Gestão de Prazos:** Controle datas e horários de execução
• **Priorização:** Organize por urgência e importância
• **Acompanhamento:** Status de pendente até concluída
• **Notificações:** Alertas para não perder prazos importantes`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Para que servem as Tarefas:**

• **PLANEJAR ações futuras** - organizar o que precisa ser feito
• **CONTROLAR prazos** - garantir execução no tempo certo
• **PRIORIZAR esforços** - focar no que é mais importante
• **ACOMPANHAR progresso** - ver o que foi feito e o que falta
• **ORGANIZAR equipe** - distribuir responsabilidades claramente

**Exemplos Práticos de Tarefas:**

**Exemplos CORRETOS (Tarefas):**
• "Ligar para Maria Santos amanhã às 14h - follow-up proposta"
• "Enviar proposta comercial para Cliente XYZ até sexta-feira"
• "Agendar reunião com diretor da empresa ABC para próxima semana"  
• "Fazer follow-up do orçamento enviado na segunda-feira"
• "Preparar apresentação para demo do produto - prazo: 3 dias"
• "Enviar contrato assinado para departamento jurídico"

**Diferença Crucial:**
- TAREFA: "Preciso ligar para cliente" (futuro - VAI FAZER)
- ATIVIDADE: "Liguei para o cliente" (passado - JÁ FEZ)

**Estados de uma Tarefa:**
Pendente → Em Andamento → Concluída`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Criando Nova Tarefa:**

**1. Clique "Nova Tarefa"**
**2. Preencha as Informações:**
• **Título:** Ação específica e clara
• **Descrição:** Detalhes do que deve ser feito
• **Lead/Cliente:** A quem se refere
• **Prioridade:** Alta, Média ou Baixa
• **Prazo:** Data e hora limite
• **Responsável:** Quem deve executar

**3. Defina Status Inicial:**
• **Pendente:** Ainda não iniciada
• **Em Andamento:** Sendo executada  
• **Concluída:** Finalizada com sucesso

**4. Execute e Atualize:**
• Mude status conforme progresso
• Ao concluir: registre resultado como ATIVIDADE

**Exemplo de Tarefa Bem Criada:**
- **Título:** "Follow-up proposta Cliente ABC"
- **Descrição:** "Ligar para verificar se recebeu a proposta de R$ 25.000 enviada na terça. Esclarecer dúvidas se houver."
- **Prazo:** 18/09/2024 09:00
- **Prioridade:** Alta (proposta vence em 2 dias)
- **Status:** Pendente `
        },
        'dicas': {
          title: 'Dicas',
          content: `**Boas Práticas para Tarefas:**

• **Seja ESPECÍFICO:** "Ligar cliente" vs "Ligar João Silva sobre proposta X às 14h"
• **Defina PRAZOS realistas:** Considere sua agenda e prioridades  
• **Use PRIORIDADES:** Alta/Média/Baixa para organizar execução
• **Atualize STATUS:** Mantenha sempre atual o progresso
• **TRANSFORME em atividade:** Tarefa concluída vira registro histórico

**Organizando sua Lista:**
- Comece sempre pelas tarefas de prioridade ALTA
- Agrupe tarefas relacionadas ao mesmo cliente
- Use filtros por data para ver agenda do dia
- Configure lembretes para tarefas críticas

**Fluxo Ideal:**
1. **TAREFA:** "Ligar para cliente Y hoje"
2. **EXECUÇÃO:** Vendedor liga para o cliente
3. **ATIVIDADE:** "Ligação realizada - 20min - cliente interessado"
4. **TAREFA:** Marcada como "Concluída"

**Relação Tarefas ↔ Atividades:**
- TAREFA concluída → gera ATIVIDADE automática
- ATIVIDADE pode → gerar nova TAREFA (próximos passos)
- Ambas se complementam no processo de vendas`
        }
      }
    },
    'contract-intelligence': {
      title: 'Contract Intelligence',
      icon: <Ico e="📋" bare />,
      description: 'Análise inteligente de contratos e documentos com IA',
      tabs: {
        'visao-geral': {
          title: 'Visão Geral',
          content: `O Contract Intelligence revoluciona a análise de documentos jurídicos usando inteligência artificial avançada. Extrai, interpreta e analisa contratos automaticamente, economizando horas de trabalho manual.

**Capacidades da IA:**

• **OCR Avançado:** Extrai texto de PDFs e imagens
• **Análise Semântica:** Compreende contexto e significado
• **Detecção de Riscos:** Identifica cláusulas problemáticas
• **Extração de Dados:** Prazos, valores, partes envolvidas
• **Comparação:** Analisa múltiplos documentos simultaneamente`
        },
        'finalidade': {
          title: 'Finalidade',
          content: `**Problemas que Resolve:**

• **Tempo Perdido:** Elimina horas de leitura manual
• **Erros Humanos:** Reduz falhas na interpretação
• **Prazos Perdidos:** Identifica datas críticas automaticamente
• **Riscos Ocultos:** Detecta cláusulas perigosas
• **Desorganização:** Centraliza informações contratuais

**Tipos de Documentos:**
- Contratos comerciais e de prestação de serviços
- Propostas e orçamentos detalhados
- Termos de uso e políticas de privacidade  
- NDAs e acordos de confidencialidade
- Documentos jurídicos em geral`
        },
        'como-usar': {
          title: 'Como Usar',
          content: `**Upload e Análise:**

**1. Preparar Documento:**
• Formatos aceitos: PDF, DOC, DOCX, JPG, PNG
• Qualidade mínima para OCR
• Tamanho máximo: 10MB por arquivo

**2. Upload no Sistema:**
• Arraste arquivo para área de upload
• Ou clique para selecionar arquivo
• Aguarde processamento inicial

**3. Análise IA:**
• Sistema processa automaticamente
• Extrai informações-chave
• Identifica riscos e oportunidades
• Gera resumo executivo

**4. Revisão e Ação:**
• Analise insights fornecidos
• Verifique pontos de atenção destacados
• Exporte relatório se necessário
• Tome ações baseadas nas recomendações`
        },
        'dicas': {
          title: 'Dicas',
          content: `**Preparação de Documentos:**

• **Qualidade:** Use documentos com texto legível
• **Idioma:** Funciona melhor com português e inglês
• **Formato:** PDFs nativos são mais precisos que digitalizados
• **Organização:** Nomeie arquivos de forma descritiva

**Interpretando Resultados:**
- Pontos vermelhos: Riscos críticos
- Pontos amarelos: Atenção recomendada
- Pontos verdes: Cláusulas favoráveis
- Sugestões: Melhorias recomendadas

**Dicas Avançadas:**
- Compare múltiplos contratos similares
- Mantenha biblioteca de templates aprovados
- Configure alertas para renovações
- Treine equipe nos insights da IA`
        }
      }
    }
  };

  const filteredModules = Object.entries(modules).filter(([key, module]) => {
    if (!searchTerm) return true;
    return (
      module.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const currentModule = modules[selectedModule];
  const currentContent = currentModule?.tabs[activeTab]?.content || '';

  const formatContent = (content) => {
    return content.split('\n\n').map((paragraph, pIndex) => {
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return (
          <h3 key={pIndex} className="text-lg font-bold text-gray-800 mt-6 mb-3">
            {paragraph.replace(/\*\*/g, '')}
          </h3>
        );
      } else if (paragraph.includes('\n• ')) {
        const items = paragraph.split('\n• ').map(item => item.replace(/^• /, ''));
        return (
          <ul key={pIndex} className="list-disc list-inside space-y-1 mb-4 text-gray-700">
            {items.map((item, iIndex) => (
              <li key={iIndex}>{item}</li>
            ))}
          </ul>
        );
      } else {
        return (
          <p key={pIndex} className="text-gray-700 mb-4 leading-relaxed">
            {paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split('<strong>').map((part, partIndex) => {
              if (partIndex === 0) return part;
              const [bold, rest] = part.split('</strong>');
              return (
                <span key={partIndex}>
                  <strong className="font-semibold text-gray-900">{bold}</strong>
                  {rest}
                </span>
              );
            })}
          </p>
        );
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-8 py-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Tutorial do Sistema
            </h1>
            <p className="text-gray-600">
              Guia completo para utilizar todas as funcionalidades do NexoSynX
            </p>
          </div>
          <div className="flex items-center space-x-2 text-gray-500">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Centro de Ajuda</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="w-full lg:w-64 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 lg:min-h-screen">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Módulos</h2>
            <p className="text-sm text-gray-500 mb-6">Selecione um módulo para aprender</p>
            
            {/* Search */}
            <div className="mb-6">
              <Input
                placeholder="Buscar módulos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Module List */}
            <div className="space-y-2">
              {filteredModules.map(([key, module]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedModule(key);
                    setActiveTab('visao-geral');
                  }}
                  className={`w-full text-left p-3 rounded-lg flex items-center space-x-3 transition-colors ${
                    selectedModule === key
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{module.icon}</span>
                  <span className="font-medium text-sm">{module.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-8 min-w-0">
          {currentModule ? (
            <div>
              {/* Module Header */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8 mb-6">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-2xl shrink-0">
                    {currentModule.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      {currentModule.title}
                    </h2>
                    <p className="text-gray-600 text-base sm:text-lg">
                      {currentModule.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="border-b border-gray-200 overflow-x-auto nx-scrollbar">
                  <nav className="flex space-x-6 sm:space-x-8 px-4 sm:px-8 w-max min-w-full">
                    {Object.entries(currentModule.tabs).map(([tabKey, tab]) => (
                      <button
                        key={tabKey}
                        onClick={() => setActiveTab(tabKey)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                          activeTab === tabKey
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.title}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-4 sm:p-8">
                  <div className="prose max-w-none">
                    {formatContent(currentContent)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="text-6xl mb-4"><Ico e="📚" bare /></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Selecione um Módulo
              </h3>
              <p className="text-gray-600">
                Escolha um módulo na lateral para começar a aprender
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}